import { useEffect, useState } from "react";
import { agentNeedsPortalClaim } from "@aam/shared";
import { getSessionToken } from "../../hooks/useSession";
import { Button } from "../ui/Button";
import { Card, CardDescription, CardHeader, CardTitle } from "../ui/Card";
import { FieldHelp, Input, Label } from "../ui/Input";
import { API_BASE } from "./types";

interface AgentClaimPanelProps {
  agentId: string;
  displayName: string;
  email: string;
  canLogin?: boolean;
  onClaimed: (email: string) => void;
}

interface ClaimStatus {
  needsClaim: boolean;
  placeholderEmail: string;
  activeInvite: {
    inviteUrl: string;
    expiresAt: string;
    createdAt: string;
  } | null;
}

export function AgentClaimPanel({
  agentId,
  displayName,
  email,
  canLogin,
  onClaimed,
}: AgentClaimPanelProps) {
  const token = getSessionToken();
  const [claimEmail, setClaimEmail] = useState("");
  const [status, setStatus] = useState<ClaimStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const needsClaim = agentNeedsPortalClaim({ email, canLogin });

  useEffect(() => {
    if (!token || !needsClaim) {
      setLoading(false);
      return;
    }

    async function loadStatus() {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/agents/${agentId}/claim`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (!res.ok || !json.success) throw new Error(json.error || "Failed to load claim status");
        setStatus(json.data as ClaimStatus);
      } catch (err) {
        setMessage({
          type: "error",
          text: err instanceof Error ? err.message : "Failed to load claim status",
        });
      } finally {
        setLoading(false);
      }
    }

    void loadStatus();
  }, [agentId, token, needsClaim]);

  if (!needsClaim) return null;

  async function createInvite() {
    if (!token) return;
    setCreatingInvite(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE}/api/agents/${agentId}/claim-invite`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to create claim link");
      setStatus((current) =>
        current
          ? {
              ...current,
              activeInvite: {
                inviteUrl: json.data.inviteUrl,
                expiresAt: json.data.expiresAt,
                createdAt: json.data.createdAt,
              },
            }
          : current
      );
      setMessage({
        type: "success",
        text: json.data.reused ? "Using existing claim link." : "Claim link created.",
      });
    } catch (err) {
      setMessage({
        type: "error",
        text: err instanceof Error ? err.message : "Failed to create claim link",
      });
    } finally {
      setCreatingInvite(false);
    }
  }

  async function copyInviteLink() {
    const inviteUrl = status?.activeInvite?.inviteUrl;
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setMessage({ type: "error", text: "Unable to copy link." });
    }
  }

  async function claimWithEmail() {
    if (!token) return;
    setClaiming(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE}/api/agents/${agentId}/claim`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: claimEmail.trim() }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to claim agent");
      onClaimed(json.data.email as string);
      setMessage({
        type: "success",
        text: "Portal email assigned. A welcome email was sent when delivery is configured.",
      });
      setClaimEmail("");
      setStatus((current) => (current ? { ...current, needsClaim: false, activeInvite: null } : current));
    } catch (err) {
      setMessage({
        type: "error",
        text: err instanceof Error ? err.message : "Failed to claim agent",
      });
    } finally {
      setClaiming(false);
    }
  }

  return (
    <Card className="border-status-nigo-border bg-status-nigo-bg/30">
      <CardHeader>
        <CardTitle className="text-base">Claim agent portal access</CardTitle>
        <CardDescription>
          {displayName} was synced from an upstream system and still uses a placeholder email (
          <span className="font-mono text-xs">{email}</span>). Assign a real address or send a claim
          link so the agent can connect their own login.
        </CardDescription>
      </CardHeader>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="space-y-3">
          <p className="text-sm font-medium text-content-primary">Assign email now</p>
          <FieldHelp>
            Staff shortcut: set the agent&apos;s portal email immediately. They can sign in with Google
            or request a magic link using this address.
          </FieldHelp>
          <Label>
            Agent email
            <Input
              className="mt-1.5"
              type="email"
              value={claimEmail}
              onChange={(event) => {
                setClaimEmail(event.target.value);
                setMessage(null);
              }}
              placeholder="agent@example.com"
            />
          </Label>
          <Button
            type="button"
            onClick={() => void claimWithEmail()}
            disabled={claiming || !claimEmail.trim()}
          >
            {claiming ? "Assigning…" : "Assign email & enable portal"}
          </Button>
        </section>

        <section className="space-y-3">
          <p className="text-sm font-medium text-content-primary">Send a claim link</p>
          <FieldHelp>
            The agent opens this link, signs in with Google or email + password, and their login is
            attached to this record ({agentId.slice(0, 8)}…).
          </FieldHelp>
          {loading ? (
            <p className="text-sm text-content-muted">Loading claim link…</p>
          ) : status?.activeInvite ? (
            <div className="space-y-2">
              <Input readOnly value={status.activeInvite.inviteUrl} className="font-mono text-xs" />
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="secondary" size="sm" onClick={() => void copyInviteLink()}>
                  {copied ? "Copied" : "Copy claim link"}
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => void createInvite()}>
                  Refresh link
                </Button>
              </div>
              <p className="text-xs text-content-muted">
                Expires {new Date(status.activeInvite.expiresAt).toLocaleString()}
              </p>
            </div>
          ) : (
            <Button type="button" variant="secondary" onClick={() => void createInvite()} disabled={creatingInvite}>
              {creatingInvite ? "Creating…" : "Create claim link"}
            </Button>
          )}
        </section>
      </div>

      {message && (
        <p
          className={
            message.type === "success" ? "mt-4 text-sm text-status-active-text" : "mt-4 text-sm text-status-rejected-text"
          }
        >
          {message.text}
        </p>
      )}
    </Card>
  );
}
